
import { tryParse, op_UnaryNegation, toString, min, max, op_Modulus, get_IsZero, equals, get_Sign, op_Division, op_LeftShift, op_Multiply, op_Subtraction, fromZero, op_Addition, op_RightShift, fromOne, toInt32, fromInt32, compare } from "../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { warn } from "../Shared/Log.js";
import { FSharpRef, Record, Union } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { class_type, list_type, record_type, int32_type, getUnionCases, makeUnion, bigint_type, union_type, string_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { filter as filter_2, collect as collect_1, append, singleton, pairwise, item, tail, length, head, isEmpty as isEmpty_1, empty, sort, cons, reverse, contains, map as map_2, tryPick, ofArray, tryFind } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { map } from "../fable_modules/fable-library-js.5.13.0/Array.js";
import { stringHash, comparePrimitives, safeHash, compare as compare_1, int32ToString, equals as equals_1 } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { Result_Map, FSharpResult$2 } from "../fable_modules/fable-library-js.5.13.0/Result.js";
import { value as value_6, defaultArg } from "../fable_modules/fable-library-js.5.13.0/Option.js";
import { isEmpty, ofList, add, filter, toList, tryFind as tryFind_1, map as map_1 } from "../fable_modules/fable-library-js.5.13.0/Map.js";
import { cToInt } from "./Ids.js";
import { printf, toText, concat, join } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { matches, create, isMatch } from "../fable_modules/fable-library-js.5.13.0/RegExp.js";
import { map as map_3, toList as toList_1, filter as filter_1, toArray, fold } from "../fable_modules/fable-library-js.5.13.0/Seq.js";
import { parse } from "../fable_modules/fable-library-js.5.13.0/Int32.js";
import { List_distinct, distinct } from "../fable_modules/fable-library-js.5.13.0/Seq2.js";

export const Constants_maxShiftCount = 16384;

/**
 * The value as an int, or None when it is too large to be one.
 * 
 * Every parameter value that reaches a component field narrower than a bigint goes through here.
 * A width, an index and a bit position are all `int` in ComponentType, and silently wrapping a
 * value that does not fit is how a nonsensical width would otherwise reach the canvas.
 * NumberHelpers.convertBigintToInt32 masks with 0xffffffff instead, so is not this.
 */
export function tryIntOfParamInt(value) {
    if ((compare(value, fromInt32(-2147483648)) >= 0) && (compare(value, fromInt32(2147483647)) <= 0)) {
        return ~~toInt32(value);
    }
    else {
        return undefined;
    }
}

/**
 * The value as an int, falling back to `ifTooLarge` where it is not one.
 * 
 * For the places that have no way to refuse: a component is being created, and its width has
 * already passed the constraints on the box it was typed into - every one of which bounds the
 * width at or below NumberHelpers.Constants.maxIssieBusWidth. So the fallback is a bug path, and
 * the warning is there to say which bound went missing rather than to be read in normal use.
 */
export function intOfParamInt(ifTooLarge, value) {
    const matchValue = tryIntOfParamInt(value);
    if (matchValue == null) {
        warn(`Parameter value ${value} is too large to be a component field: using ${ifTooLarge}`);
        return ifTooLarge | 0;
    }
    else {
        return matchValue | 0;
    }
}

/**
 * A named parameter in a custom component type
 * For MVP this is ok but maybe names need to be qualified by the
 * design sheet they are in to make functions support parameter inheritance.
 */
export class ParamName extends Union {
    constructor(Item) {
        super();
        this.tag = 0;
        this.fields = [Item];
    }
    cases() {
        return ["ParamName"];
    }
}

export function ParamName_$reflection() {
    return union_type("ParameterTypes.ParamName", [], ParamName, () => [[["Item", string_type]]]);
}

/**
 * A built-in function of two arguments, named by an enumeration so that adding one is a case here
 * rather than a case of ParamExpression. Every list of these is derived from the type by
 * EEExtensions.Union.allCases, so adding a case reserves its name and reaches the parser at once;
 * the compiler then requires only that binFuncName covers it.
 */
export class ParamBinFunc extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["PMin", "PMax"];
    }
    static PMin = new ParamBinFunc(0, []);
    static PMax = new ParamBinFunc(1, []);
}

export function ParamBinFunc_$reflection() {
    return union_type("ParameterTypes.ParamBinFunc", [], ParamBinFunc, () => [[], []]);
}

/**
 * An arithmetic expression containing symbolic parameters
 * For MVP this could be limited to PInt and PParameter only.
 * However, it would be useful to have a more general type definition so that
 * functions that manipulate constraints, parameters, etc can be written in a more general way.
 * The actual parameter value is customisable so that the same code can be used for int parameters (normal)
 * and BigInt parameters (needed for constant values in N bit components).
 * For MVP set 'PINT = int
 * TODO: refactor this to use an enumeration DU for operators to reduce cases.
 * 
 * The built-in functions are already in that shape: PBinFunc names its operation with an
 * enumeration rather than taking a case of its own per function.
 */
export class ParamExpression extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["PInt", "PParameter", "PAdd", "PSubtract", "PMultiply", "PDivide", "PRemainder", "PShiftLeft", "PShiftRight", "PCLog2", "PBinFunc"];
    }
}

export function ParamExpression_$reflection() {
    return union_type("ParameterTypes.ParamExpression", [], ParamExpression, () => [[["Item", bigint_type]], [["Item", ParamName_$reflection()]], [["Item1", ParamExpression_$reflection()], ["Item2", ParamExpression_$reflection()]], [["Item1", ParamExpression_$reflection()], ["Item2", ParamExpression_$reflection()]], [["Item1", ParamExpression_$reflection()], ["Item2", ParamExpression_$reflection()]], [["Item1", ParamExpression_$reflection()], ["Item2", ParamExpression_$reflection()]], [["Item1", ParamExpression_$reflection()], ["Item2", ParamExpression_$reflection()]], [["Item1", ParamExpression_$reflection()], ["Item2", ParamExpression_$reflection()]], [["Item1", ParamExpression_$reflection()], ["Item2", ParamExpression_$reflection()]], [["Item", ParamExpression_$reflection()]], [["Item1", ParamBinFunc_$reflection()], ["Item2", ParamExpression_$reflection()], ["Item3", ParamExpression_$reflection()]]]);
}

/**
 * The name a two-argument built-in is written under. Lower case: this is the canonical spelling
 * that renderParamExpression emits, though the parser accepts any case.
 */
export function binFuncName(func) {
    if (func.tag === 1) {
        return "max";
    }
    else {
        return "min";
    }
}

export const cLog2Name = "clog2";

/**
 * The two-argument built-in of that name, matched without regard to case.
 */
export function tryBuiltinBinFunc(name) {
    return tryFind((func) => (binFuncName(func) === name.toLowerCase()), ofArray(map((case$) => makeUnion(case$, []), getUnionCases(ParamBinFunc_$reflection()))));
}

/**
 * Whether the name is a built-in function. Matched without regard to case, so that `MAX` cannot be
 * a parameter while `max` is a function: the two would read as the same word.
 */
export function isBuiltinFuncName(name) {
    if (name.toLowerCase() === cLog2Name) {
        return true;
    }
    else {
        return tryBuiltinBinFunc(name) != null;
    }
}

/**
 * Bits needed to index n things: ceil(log2 n), so clog2 8 = 3 and clog2 9 = 4.
 * 0 and 1 both give 0, as Verilog's $clog2 does.
 * 
 * The n <= 1 guard is load-bearing rather than tidiness: >>> on a negative bigint is an arithmetic
 * shift and never reaches 0, so without it a negative argument would loop for ever. The evaluator
 * rejects negatives before calling this, which is where the user-facing message lives.
 * 
 * Computed by shifting rather than by System.Math.Log: the float version is wrong at exact powers
 * of two, which is why the one in SheetCreator is commented out.
 */
export function clog2(n) {
    const bits = (v_mut, acc_mut) => {
        bits:
        while (true) {
            const v = v_mut, acc = acc_mut;
            if (compare(v, fromOne()) <= 0) {
                return acc;
            }
            else {
                v_mut = op_RightShift(v, 1);
                acc_mut = op_Addition(acc, fromOne());
                continue bits;
            }
            break;
        }
    };
    if (compare(n, fromOne()) <= 0) {
        return fromZero();
    }
    else {
        return op_Addition(bits(op_Subtraction(n, fromOne()), fromZero()), fromOne());
    }
}

/**
 * The value of `value<<places`: multiplication by 2^places.
 * 
 * Written as a multiplication rather than with `<<<` so that the two runtimes cannot disagree
 * about it. Fable's bigint is its own implementation, and it is a NEGATIVE left operand - which
 * nothing else in Issie shifts - whose sign-magnitude representation might move differently there
 * than under .NET, where no test would see it. Multiplying is the same operation in both.
 * Both callers bound places by Constants.maxShiftCount before calling.
 */
export function shiftLeftBy(places, value) {
    return op_Multiply(value, op_LeftShift(fromOne(), places));
}

/**
 * The value of `value>>places`: division by 2^places, rounding towards minus infinity, which is
 * what makes the shift arithmetic.
 * 
 * bigint division truncates towards zero instead, so `-1/2` is 0 where an arithmetic `-1>>1` is
 * -1. Taking the truncating quotient and stepping it down where it threw something away is the
 * same rounding for every value, and is again the same in both runtimes.
 */
export function shiftRightBy(places, value) {
    const divisor = op_LeftShift(fromOne(), places);
    const quotient = op_Division(value, divisor);
    if ((get_Sign(value) < 0) && !equals(op_Multiply(quotient, divisor), value)) {
        return op_Subtraction(quotient, fromOne());
    }
    else {
        return quotient;
    }
}

/**
 * For MVP could allow only PInt case constraints
 * The Errors are human-readable explanations of why violating the constraint is not allowed.
 * They should if possible be component-specific "constant MyConstName is 3 bit width so not allowed to be less than -4".
 */
export class ParamConstraint extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["MinVal", "MaxVal"];
    }
}

export function ParamConstraint_$reflection() {
    return union_type("ParameterTypes.ParamConstraint", [], ParamConstraint, () => [[["Item1", ParamExpression_$reflection()], ["Item2", string_type]], [["Item1", ParamExpression_$reflection()], ["Item2", string_type]]]);
}

/**
 * A string marking a specific integer value in a case of ComponentType.
 * The values here are arbitrary and ComponentType-case specific and all that matters is that each value is unique
 * within the case.
 * The number of inputs of a gate or merge is deliberately absent: an input count sets how many
 * ports a component has, and a parameter records a value, not a change of topology.
 */
export class CompSlotName extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Buswidth", "IO", "CustomCompParam", "SplitNWidth", "SplitNLSB", "InputDefault", "MemoryAddressWidth", "MemoryWordWidth", "JoinNum"];
    }
    static Buswidth = new CompSlotName(0, []);
    static InputDefault = new CompSlotName(5, []);
    static MemoryAddressWidth = new CompSlotName(6, []);
    static MemoryWordWidth = new CompSlotName(7, []);
    static JoinNum = new CompSlotName(8, []);
}

export function CompSlotName_$reflection() {
    return union_type("ParameterTypes.CompSlotName", [], CompSlotName, () => [[], [["Label", string_type]], [["ParamName", string_type]], [["Index", int32_type]], [["Index", int32_type]], [], [], [], []]);
}

/**
 * A slot in a component instance that can be bound to a parameter expression.
 * ComponentId 0 is the sentinel for "no component" - a popup's dialog box.
 */
export class ParamSlot extends Record {
    constructor(CompId, CompSlot) {
        super();
        this.CompId = CompId;
        this.CompSlot = CompSlot;
    }
}

export function ParamSlot_$reflection() {
    return record_type("ParameterTypes.ParamSlot", [], ParamSlot, () => [["CompId", int32_type], ["CompSlot", CompSlotName_$reflection()]]);
}

export const compId_ = [(s) => s.CompId, (v) => ((s_1) => (new ParamSlot(v, s_1.CompSlot)))];

export const compSlot_ = [(s) => s.CompSlot, (v) => ((s_1) => (new ParamSlot(s_1.CompId, v)))];

/**
 * Whether two slot names refer to the same field of a component.
 * 
 * The label in an `IO` slot is not part of its identity. It records the component's label as it
 * was when the slot was created, and nothing rewrites it when the component is renamed - so a
 * rename would otherwise orphan the slot and let a second one be created for the same field, with
 * which of the two applied decided by Map key order. Every reader already ignores it
 * (ComponentSlots.trySetSlotValue matches `IO _` in every case), so this is what "the same slot"
 * has always meant in effect. The label is kept in the type because existing .dgm files store it,
 * and it is repaired on save by CanvasExtractor.tidyParamSlots so that it stays worth displaying.
 */
export function sameSlotName(a, b) {
    let matchResult;
    if (a.tag === 1) {
        if (b.tag === 1) {
            matchResult = 0;
        }
        else {
            matchResult = 1;
        }
    }
    else {
        matchResult = 1;
    }
    switch (matchResult) {
        case 0:
            return true;
        default:
            return equals_1(a, b);
    }
}

/**
 * Whether two slots are the same slot: the same field of the same component. See sameSlotName.
 */
export function sameSlot(a, b) {
    if (equals_1(a.CompId, b.CompId)) {
        return sameSlotName(a.CompSlot, b.CompSlot);
    }
    else {
        return false;
    }
}

/**
 * A parameter expression and its corresponding constraints
 */
export class ConstrainedExpr extends Record {
    constructor(Expression, Constraints) {
        super();
        this.Expression = Expression;
        this.Constraints = Constraints;
    }
}

export function ConstrainedExpr_$reflection() {
    return record_type("ParameterTypes.ConstrainedExpr", [], ConstrainedExpr, () => [["Expression", ParamExpression_$reflection()], ["Constraints", list_type(ParamConstraint_$reflection())]]);
}

export const expression_ = [(s) => s.Expression, (v) => ((s_1) => (new ConstrainedExpr(v, s_1.Constraints)))];

export const constraints_ = [(s) => s.Constraints, (v) => ((s_1) => (new ConstrainedExpr(s_1.Expression, v)))];

/**
 * Data for a new parameterised slot being created
 */
export class NewParamCompSpec extends Record {
    constructor(CompSlot, Expression, Constraints, Value) {
        super();
        this.CompSlot = CompSlot;
        this.Expression = Expression;
        this.Constraints = Constraints;
        this.Value = Value;
    }
}

export function NewParamCompSpec_$reflection() {
    return record_type("ParameterTypes.NewParamCompSpec", [], NewParamCompSpec, () => [["CompSlot", CompSlotName_$reflection()], ["Expression", ParamExpression_$reflection()], ["Constraints", list_type(ParamConstraint_$reflection())], ["Value", bigint_type]]);
}

export const newParamCompSlot_ = [(s) => s.CompSlot, (v) => ((s_1) => (new NewParamCompSpec(v, s_1.Expression, s_1.Constraints, s_1.Value)))];

export const newParamExpression_ = [(s) => s.Expression, (v) => ((s_1) => (new NewParamCompSpec(s_1.CompSlot, v, s_1.Constraints, s_1.Value)))];

export const newParamConstraints_ = [(s) => s.Constraints, (v) => ((s_1) => (new NewParamCompSpec(s_1.CompSlot, s_1.Expression, v, s_1.Value)))];

export const newParamValue_ = [(s) => s.Value, (v) => ((s_1) => (new NewParamCompSpec(s_1.CompSlot, s_1.Expression, s_1.Constraints, v)))];

/**
 * What one property input box holds while it is being edited: the text as typed, and what that
 * text means.
 * 
 * The TEXT is model state, as it is for every other input box in Issie - the Constant's value box,
 * the component name box - which dispatch the unparsed string on each keystroke and render
 * themselves from it. Keeping only the parsed value here left the box with no way to be set except
 * by reaching past React into the DOM, which is what the offer to bind a property to an enclosing
 * sheet's had to do.
 * 
 * Only a Spec that is Ok ever reaches a component: text that will not parse, will not evaluate, or
 * breaks a constraint stays here with its message, and the design is untouched until it is valid.
 */
export class ParamBoxState extends Record {
    constructor(Text$, Spec) {
        super();
        this.Text = Text$;
        this.Spec = Spec;
    }
}

export function ParamBoxState_$reflection() {
    return record_type("ParameterTypes.ParamBoxState", [], ParamBoxState, () => [["Text", string_type], ["Spec", union_type("Microsoft.FSharp.Core.FSharpResult`2", [NewParamCompSpec_$reflection(), string_type], FSharpResult$2, () => [[["ResultValue", NewParamCompSpec_$reflection()]], [["ErrorValue", string_type]]])]]);
}

/**
 * The dialog-state key for a box belonging to a component, or to a popup where there is none.
 * 0 is never a real component id - the allocator hands out ids from 1 - so it means "popup".
 */
export function paramBoxKey(compId, slot) {
    return new ParamSlot(defaultArg(compId, 0), slot);
}

/**
 * The DECLARATION of one parameter on a sheet: its default value and what it means.
 * The description is compulsory - it is what the user reads when a custom component instance of
 * the sheet asks them for a value, so a parameter without one cannot be explained at the point
 * it has to be understood.
 */
export class ParamDefinition extends Record {
    constructor(Expression, Description) {
        super();
        this.Expression = Expression;
        this.Description = Description;
    }
}

export function ParamDefinition_$reflection() {
    return record_type("ParameterTypes.ParamDefinition", [], ParamDefinition, () => [["Expression", ParamExpression_$reflection()], ["Description", string_type]]);
}

export const paramExpression_ = [(s) => s.Expression, (v) => ((s_1) => (new ParamDefinition(v, s_1.Description)))];

export const paramDescription_ = [(s) => s.Description, (v) => ((s_1) => (new ParamDefinition(s_1.Expression, v)))];

/**
 * The declarations seen as an evaluation environment: descriptions dropped.
 * Every place that evaluates an expression against a sheet's defaults goes through this.
 */
export function bindingsOf(defs) {
    return map_1((_arg, def) => def.Expression, defs);
}

/**
 * The expression filling a slot, found by what the slot IS rather than by the exact key: an `IO`
 * slot stored under the component's old label is still that component's IO slot. See sameSlot.
 */
export function tryFindSlot(slot, slots) {
    const matchValue = tryFind_1(slot, slots);
    if (matchValue == null) {
        return tryPick((tupledArg) => {
            if (sameSlot(tupledArg[0], slot)) {
                return tupledArg[1];
            }
            else {
                return undefined;
            }
        }, toList(slots));
    }
    else {
        return matchValue;
    }
}

function withoutSlot(slot, slots) {
    return filter((s, _arg) => !sameSlot(s, slot), slots);
}

/**
 * Put an expression in a slot, replacing whatever filled it before.
 */
export function addSlot(slot, exprSpec, slots) {
    return add(slot, exprSpec, withoutSlot(slot, slots));
}

/**
 * Empty a slot, so that its field goes back to being an ordinary number.
 */
export function removeSlot(slot, slots) {
    return withoutSlot(slot, slots);
}

/**
 * The state used per design sheet to define integer slots
 * that have values defined with parameter expressions
 * LoadedComponent.LCParameterSlots
 * (also used in SheetInfo - to save / load files - but the LoadedComponent field is the only one used by HLP Teams)
 */
export class ParameterDefs extends Record {
    constructor(DefaultBindings, ParamSlots) {
        super();
        this.DefaultBindings = DefaultBindings;
        this.ParamSlots = ParamSlots;
    }
}

export function ParameterDefs_$reflection() {
    return record_type("ParameterTypes.ParameterDefs", [], ParameterDefs, () => [["DefaultBindings", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [ParamName_$reflection(), ParamDefinition_$reflection()])], ["ParamSlots", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [ParamSlot_$reflection(), ConstrainedExpr_$reflection()])]]);
}

export const defaultBindings_ = [(s) => s.DefaultBindings, (v) => ((s_1) => (new ParameterDefs(v, s_1.ParamSlots)))];

export const paramSlots_ = [(s) => s.ParamSlots, (v) => ((s_1) => (new ParameterDefs(s_1.DefaultBindings, v)))];

export class JSONParams_ParamSlot extends Record {
    constructor(CompId, CompSlot) {
        super();
        this.CompId = CompId;
        this.CompSlot = CompSlot;
    }
}

export function JSONParams_ParamSlot_$reflection() {
    return record_type("ParameterTypes.JSONParams.ParamSlot", [], JSONParams_ParamSlot, () => [["CompId", string_type], ["CompSlot", CompSlotName_$reflection()]]);
}

export class JSONParams_ParameterDefs extends Record {
    constructor(DefaultBindings, ParamSlots) {
        super();
        this.DefaultBindings = DefaultBindings;
        this.ParamSlots = ParamSlots;
    }
}

export function JSONParams_ParameterDefs_$reflection() {
    return record_type("ParameterTypes.JSONParams.ParameterDefs", [], JSONParams_ParameterDefs, () => [["DefaultBindings", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [ParamName_$reflection(), ParamDefinition_$reflection()])], ["ParamSlots", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [JSONParams_ParamSlot_$reflection(), ConstrainedExpr_$reflection()])]]);
}

/**
 * The saved form of a slot map, component ids written as decimal strings.
 */
export function slotsToJson(slots) {
    return ofList(map_2((tupledArg) => {
        const slot = tupledArg[0];
        return [new JSONParams_ParamSlot(int32ToString(cToInt(slot.CompId)), slot.CompSlot), tupledArg[1]];
    }, toList(slots)), {
        Compare: (x, y) => (compare_1(x, y) | 0),
    });
}

/**
 * A saved slot map read back, id strings mapped by the loader's rule.
 */
export function slotsOfJson(mapCompId, slots) {
    return ofList(map_2((tupledArg) => {
        const slot = tupledArg[0];
        return [new ParamSlot(mapCompId(slot.CompId), slot.CompSlot), tupledArg[1]];
    }, toList(slots)), {
        Compare: (x, y) => (compare_1(x, y) | 0),
    });
}

export function paramDefsToJson(defs) {
    return new JSONParams_ParameterDefs(defs.DefaultBindings, slotsToJson(defs.ParamSlots));
}

export function paramDefsOfJson(mapCompId, defs) {
    return new ParameterDefs(defs.DefaultBindings, slotsOfJson(mapCompId, defs.ParamSlots));
}

/**
 * Evaluates a parameter expression given a set of parameter bindings.
 */
export function evaluateParamExpression(paramBindings, paramExpr) {
    const unwrapParamName = (_arg) => _arg.fields[0];
    const eval$ = (beingEvaluated_mut, expr_mut) => {
        eval$:
        while (true) {
            const beingEvaluated = beingEvaluated_mut, expr = expr_mut;
            const binary = (combine, left, right) => {
                const matchValue = eval$(beingEvaluated, left);
                const matchValue_1 = eval$(beingEvaluated, right);
                const copyOfStruct = matchValue;
                if (copyOfStruct.tag === 1) {
                    return new FSharpResult$2(/* Error */ 1, [copyOfStruct.fields[0]]);
                }
                else {
                    const copyOfStruct_1 = matchValue_1;
                    if (copyOfStruct_1.tag === 1) {
                        return new FSharpResult$2(/* Error */ 1, [copyOfStruct_1.fields[0]]);
                    }
                    else {
                        return combine(copyOfStruct.fields[0])(copyOfStruct_1.fields[0]);
                    }
                }
            };
            const shift = (op, apply, left_1, right_1) => binary((l_1) => ((r_1) => ((get_Sign(r_1) < 0) ? (new FSharpResult$2(/* Error */ 1, [`Shift by a negative number of places: ${l_1}${op}${r_1} cannot shift the other way`])) : ((compare(r_1, fromInt32(Constants_maxShiftCount)) > 0) ? (new FSharpResult$2(/* Error */ 1, [`Shift too far: ${l_1}${op}${r_1} moves further than the ${Constants_maxShiftCount} bits of the widest bus`])) : (new FSharpResult$2(/* Ok */ 0, [apply(~~toInt32(r_1))(l_1)]))))), left_1, right_1);
            switch (expr.tag) {
                case 1:
                    if (contains(expr.fields[0], beingEvaluated, {
                        Equals: equals_1,
                        GetHashCode: (x) => (safeHash(x) | 0),
                    })) {
                        const chain = join(" which uses ", map_2(unwrapParamName, reverse(cons(expr.fields[0], beingEvaluated))));
                        return new FSharpResult$2(/* Error */ 1, [concat("Property \'", unwrapParamName(expr.fields[0]), "\' is defined in terms of itself: ", chain)]);
                    }
                    else {
                        const matchValue_3 = tryFind_1(expr.fields[0], paramBindings);
                        if (matchValue_3 == null) {
                            if (isEmpty(paramBindings)) {
                                return new FSharpResult$2(/* Error */ 1, ["This value must be numeric: to use a property this must first be added to the sheet"]);
                            }
                            else {
                                const inScope = join(", ", sort(map_2((arg) => unwrapParamName(arg[0]), toList(paramBindings)), {
                                    Compare: (x_1, y_1) => (comparePrimitives(x_1, y_1) | 0),
                                }));
                                return new FSharpResult$2(/* Error */ 1, [concat("Property \'", unwrapParamName(expr.fields[0]), "\' is not defined. Properties of this sheet: ", inScope)]);
                            }
                        }
                        else {
                            beingEvaluated_mut = cons(expr.fields[0], beingEvaluated);
                            expr_mut = matchValue_3;
                            continue eval$;
                        }
                    }
                case 2:
                    return binary((l_2) => ((r_2) => (new FSharpResult$2(/* Ok */ 0, [op_Addition(l_2, r_2)]))), expr.fields[0], expr.fields[1]);
                case 3:
                    return binary((l_3) => ((r_3) => (new FSharpResult$2(/* Ok */ 0, [op_Subtraction(l_3, r_3)]))), expr.fields[0], expr.fields[1]);
                case 4:
                    return binary((l_4) => ((r_4) => (new FSharpResult$2(/* Ok */ 0, [op_Multiply(l_4, r_4)]))), expr.fields[0], expr.fields[1]);
                case 5:
                    return binary((l_5) => ((r_5) => (get_IsZero(r_5) ? (new FSharpResult$2(/* Error */ 1, [`Division by zero: ${l_5} cannot be divided by 0`])) : (new FSharpResult$2(/* Ok */ 0, [op_Division(l_5, r_5)])))), expr.fields[0], expr.fields[1]);
                case 6:
                    return binary((l_6) => ((r_6) => (get_IsZero(r_6) ? (new FSharpResult$2(/* Error */ 1, [`Remainder by zero: the remainder of ${l_6} divided by 0 is undefined`])) : (new FSharpResult$2(/* Ok */ 0, [op_Modulus(l_6, r_6)])))), expr.fields[0], expr.fields[1]);
                case 7:
                    return shift("<<", (places) => ((value_1) => shiftLeftBy(places, value_1)), expr.fields[0], expr.fields[1]);
                case 8:
                    return shift(">>", (places_1) => ((value_2) => shiftRightBy(places_1, value_2)), expr.fields[0], expr.fields[1]);
                case 9: {
                    const matchValue_4 = eval$(beingEvaluated, expr.fields[0]);
                    if (matchValue_4.tag === 0) {
                        if (get_Sign(matchValue_4.fields[0]) < 0) {
                            return new FSharpResult$2(/* Error */ 1, [`${cLog2Name} is not defined for negative values: ${cLog2Name}(${matchValue_4.fields[0]})`]);
                        }
                        else {
                            return new FSharpResult$2(/* Ok */ 0, [clog2(matchValue_4.fields[0])]);
                        }
                    }
                    else {
                        return new FSharpResult$2(/* Error */ 1, [matchValue_4.fields[0]]);
                    }
                }
                case 10:
                    if (expr.fields[0].tag === 1) {
                        return binary((l_8) => ((r_8) => (new FSharpResult$2(/* Ok */ 0, [max(l_8, r_8)]))), expr.fields[1], expr.fields[2]);
                    }
                    else {
                        return binary((l_7) => ((r_7) => (new FSharpResult$2(/* Ok */ 0, [min(l_7, r_7)]))), expr.fields[1], expr.fields[2]);
                    }
                default:
                    return new FSharpResult$2(/* Ok */ 0, [expr.fields[0]]);
            }
            break;
        }
    };
    return eval$(empty(), paramExpr);
}

/**
 * Converts a parameter expression to its string representation with proper operator precedence.
 */
export function renderParamExpression(expr, precedence) {
    switch (expr.tag) {
        case 1:
            return expr.fields[0].fields[0];
        case 7: {
            const right = expr.fields[1];
            const left = expr.fields[0];
            if (precedence > 1) {
                return ((("(" + renderParamExpression(left, 1)) + "<<") + renderParamExpression(right, 2)) + ")";
            }
            else {
                return (renderParamExpression(left, 1) + "<<") + renderParamExpression(right, 2);
            }
        }
        case 8: {
            const right_1 = expr.fields[1];
            const left_1 = expr.fields[0];
            if (precedence > 1) {
                return ((("(" + renderParamExpression(left_1, 1)) + ">>") + renderParamExpression(right_1, 2)) + ")";
            }
            else {
                return (renderParamExpression(left_1, 1) + ">>") + renderParamExpression(right_1, 2);
            }
        }
        case 2: {
            const right_2 = expr.fields[1];
            const left_2 = expr.fields[0];
            if (precedence > 2) {
                return ((("(" + renderParamExpression(left_2, 2)) + "+") + renderParamExpression(right_2, 2)) + ")";
            }
            else {
                return (renderParamExpression(left_2, 2) + "+") + renderParamExpression(right_2, 2);
            }
        }
        case 3: {
            const right_3 = expr.fields[1];
            const left_3 = expr.fields[0];
            if (precedence > 2) {
                return ((("(" + renderParamExpression(left_3, 2)) + "-") + renderParamExpression(right_3, 3)) + ")";
            }
            else {
                return (renderParamExpression(left_3, 2) + "-") + renderParamExpression(right_3, 3);
            }
        }
        case 4: {
            const right_4 = expr.fields[1];
            const left_4 = expr.fields[0];
            if (precedence > 3) {
                return ((("(" + renderParamExpression(left_4, 3)) + "*") + renderParamExpression(right_4, 4)) + ")";
            }
            else {
                return (renderParamExpression(left_4, 3) + "*") + renderParamExpression(right_4, 4);
            }
        }
        case 5: {
            const right_5 = expr.fields[1];
            const left_5 = expr.fields[0];
            if (precedence > 3) {
                return ((("(" + renderParamExpression(left_5, 3)) + "/") + renderParamExpression(right_5, 4)) + ")";
            }
            else {
                return (renderParamExpression(left_5, 3) + "/") + renderParamExpression(right_5, 4);
            }
        }
        case 6:
            return ((("(" + renderParamExpression(expr.fields[0], 4)) + "%") + renderParamExpression(expr.fields[1], 4)) + ")";
        case 9:
            return ((cLog2Name + "(") + renderParamExpression(expr.fields[0], 0)) + ")";
        case 10:
            return ((((binFuncName(expr.fields[0]) + "(") + renderParamExpression(expr.fields[1], 0)) + ",") + renderParamExpression(expr.fields[2], 0)) + ")";
        default:
            return toString(expr.fields[0]);
    }
}

/**
 * Whether the name is one a parameter may not take because it is a built-in function.
 * Kept separate from isValidParamName so that a dialog can say which rule was broken: "clog2 is a
 * function" and "names are letters and digits" are different problems needing different fixes.
 */
export function isReservedParamName(name) {
    return isBuiltinFuncName(name);
}

const emptyInputError = "Type a number or expression";

/**
 * The names a parameter may have: a letter, then letters and digits, and not a built-in function.
 * 
 * This is exactly the parser's name token, exported so that the two cannot drift apart. They had:
 * names were accepted as `[a-zA-Z0-9]+`, while the tokenizer split `W2X` into `W2` and `X` - so a
 * parameter could be declared under a name and then never referred to. A leading digit is
 * excluded for the same reason read the other way round: `123` would be a number. A built-in
 * function name is excluded for the third form of the same reason: the parser reads `min` as the
 * function, so a parameter of that name could be declared and then never referred to.
 */
export function isValidParamName(name) {
    if (isMatch(/^[a-zA-Z][a-zA-Z0-9]*$/gu, name)) {
        return !isReservedParamName(name);
    }
    else {
        return false;
    }
}

/**
 * Parses a string into a parameter expression AST.
 */
export function parseExpression(text) {
    let bracket, arg_2;
    const parsePrimary = (tokens) => {
        let operand, inBase, matchValue, outArg;
        const parseCall = (name, arity, form, tokens_1) => {
            const parseArgs = (remaining_mut, args_mut) => {
                parseArgs:
                while (true) {
                    const remaining = remaining_mut, args = args_mut;
                    const matchValue_1 = parseExpressionTokens(remaining);
                    if (matchValue_1.tag === 0) {
                        const rest = matchValue_1.fields[0][1];
                        const args_1 = cons(matchValue_1.fields[0][0], args);
                        let matchResult, more_2, more_3;
                        if (!isEmpty_1(rest)) {
                            switch (head(rest)) {
                                case ",": {
                                    if (length(args_1) < arity) {
                                        matchResult = 0;
                                        more_2 = tail(rest);
                                    }
                                    else {
                                        matchResult = 2;
                                    }
                                    break;
                                }
                                case ")": {
                                    if (length(args_1) === arity) {
                                        matchResult = 1;
                                        more_3 = tail(rest);
                                    }
                                    else {
                                        matchResult = 2;
                                    }
                                    break;
                                }
                                default:
                                    matchResult = 2;
                            }
                        }
                        else {
                            matchResult = 2;
                        }
                        switch (matchResult) {
                            case 0: {
                                remaining_mut = more_2;
                                args_mut = args_1;
                                continue parseArgs;
                            }
                            case 1:
                                return new FSharpResult$2(/* Ok */ 0, [[reverse(args_1), more_3]]);
                            default:
                                return new FSharpResult$2(/* Error */ 1, [`${name} takes ${arity} values: write ${form}`]);
                        }
                    }
                    else {
                        return new FSharpResult$2(/* Error */ 1, [matchValue_1.fields[0]]);
                    }
                    break;
                }
            };
            let matchResult_1, rest_1;
            if (!isEmpty_1(tokens_1)) {
                if (head(tokens_1) === "(") {
                    matchResult_1 = 0;
                    rest_1 = tail(tokens_1);
                }
                else {
                    matchResult_1 = 1;
                }
            }
            else {
                matchResult_1 = 1;
            }
            switch (matchResult_1) {
                case 0:
                    return parseArgs(rest_1, empty());
                default:
                    return new FSharpResult$2(/* Error */ 1, [concat(name, " is a built-in function: write ", form)]);
            }
        };
        if (!isEmpty_1(tokens)) {
            switch (head(tokens)) {
                case "(": {
                    const matchValue_2 = parseExpressionTokens(tail(tokens));
                    let matchResult_2, expr_1, remainingTokens, e_1;
                    if (matchValue_2.tag === 1) {
                        matchResult_2 = 2;
                        e_1 = matchValue_2.fields[0];
                    }
                    else if (!isEmpty_1(matchValue_2.fields[0][1])) {
                        if (head(matchValue_2.fields[0][1]) === ")") {
                            matchResult_2 = 0;
                            expr_1 = matchValue_2.fields[0][0];
                            remainingTokens = tail(matchValue_2.fields[0][1]);
                        }
                        else {
                            matchResult_2 = 1;
                        }
                    }
                    else {
                        matchResult_2 = 1;
                    }
                    switch (matchResult_2) {
                        case 0:
                            return new FSharpResult$2(/* Ok */ 0, [[expr_1, remainingTokens]]);
                        case 1:
                            return new FSharpResult$2(/* Error */ 1, ["Mismatched parentheses"]);
                        default:
                            return new FSharpResult$2(/* Error */ 1, [e_1]);
                    }
                }
                case ")":
                    return new FSharpResult$2(/* Error */ 1, ["Unexpected closing parenthesis"]);
                case ",":
                    return new FSharpResult$2(/* Error */ 1, ["Unexpected comma: a comma separates the two values of min or max"]);
                case "-":
                    return Result_Map((tupledArg) => {
                        let expr;
                        return [(expr = tupledArg[0], (expr.tag === 0) ? (new ParamExpression(/* PInt */ 0, [op_UnaryNegation(expr.fields[0])])) : (new ParamExpression(/* PSubtract */ 3, [new ParamExpression(/* PInt */ 0, [fromZero()]), expr]))), tupledArg[1]];
                    }, parsePrimary(tail(tokens)));
                default:
                    if (head(tokens).toLowerCase() === cLog2Name) {
                        return Result_Map((tupledArg_1) => [new ParamExpression(/* PCLog2 */ 9, [item(0, tupledArg_1[0])]), tupledArg_1[1]], parseCall(cLog2Name, 1, concat(cLog2Name, "(expression)"), tail(tokens)));
                    }
                    else if (tryBuiltinBinFunc(head(tokens)) != null) {
                        const func = value_6(tryBuiltinBinFunc(head(tokens)));
                        const funcName = binFuncName(func);
                        return Result_Map((tupledArg_2) => {
                            const args_3 = tupledArg_2[0];
                            return [new ParamExpression(/* PBinFunc */ 10, [func, item(0, args_3), item(1, args_3)]), tupledArg_2[1]];
                        }, parseCall(funcName, 2, concat(funcName, "(a,b)"), tail(tokens)));
                    }
                    else {
                        return new FSharpResult$2(/* Ok */ 0, [[(operand = head(tokens), (inBase = ((radix, digits) => fold((acc, c) => op_Addition(op_Multiply(acc, fromInt32(radix)), fromInt32(parse(c, 511, false, 32, radix))), fromZero(), digits.split(""))), (((operand.length > 2) && (operand[0] === "0")) && ((operand[1] === "x") ? true : (operand[1] === "X"))) ? (new ParamExpression(/* PInt */ 0, [inBase(16, operand.slice(2, operand.length))])) : ((((operand.length > 2) && (operand[0] === "0")) && ((operand[1] === "b") ? true : (operand[1] === "B"))) ? (new ParamExpression(/* PInt */ 0, [inBase(2, operand.slice(2, operand.length))])) : ((matchValue = ((outArg = (0n), [tryParse(operand, new FSharpRef(() => outArg, (v) => {
                            outArg = v;
                        })), outArg])), matchValue[0] ? (new ParamExpression(/* PInt */ 0, [matchValue[1]])) : (new ParamExpression(/* PParameter */ 1, [new ParamName(operand)]))))))), tail(tokens)]]);
                    }
            }
        }
        else {
            return new FSharpResult$2(/* Error */ 1, ["Unfinished expression"]);
        }
    };
    const parseFactors = (tokens_2) => {
        const matchValue_3 = parsePrimary(tokens_2);
        if (matchValue_3.tag === 1) {
            return new FSharpResult$2(/* Error */ 1, [matchValue_3.fields[0]]);
        }
        else {
            const loop = (expr_3_mut, remainingTokens_1_mut) => {
                loop:
                while (true) {
                    const expr_3 = expr_3_mut, remainingTokens_1 = remainingTokens_1_mut;
                    let matchResult_3, rest_10, rest_11, rest_12;
                    if (!isEmpty_1(remainingTokens_1)) {
                        switch (head(remainingTokens_1)) {
                            case "*": {
                                matchResult_3 = 0;
                                rest_10 = tail(remainingTokens_1);
                                break;
                            }
                            case "/": {
                                matchResult_3 = 1;
                                rest_11 = tail(remainingTokens_1);
                                break;
                            }
                            case "%": {
                                matchResult_3 = 2;
                                rest_12 = tail(remainingTokens_1);
                                break;
                            }
                            default:
                                matchResult_3 = 3;
                        }
                    }
                    else {
                        matchResult_3 = 3;
                    }
                    switch (matchResult_3) {
                        case 0: {
                            const matchValue_4 = parsePrimary(rest_10);
                            if (matchValue_4.tag === 1) {
                                return new FSharpResult$2(/* Error */ 1, [matchValue_4.fields[0]]);
                            }
                            else {
                                expr_3_mut = (new ParamExpression(/* PMultiply */ 4, [expr_3, matchValue_4.fields[0][0]]));
                                remainingTokens_1_mut = matchValue_4.fields[0][1];
                                continue loop;
                            }
                        }
                        case 1: {
                            const matchValue_5 = parsePrimary(rest_11);
                            if (matchValue_5.tag === 1) {
                                return new FSharpResult$2(/* Error */ 1, [matchValue_5.fields[0]]);
                            }
                            else {
                                expr_3_mut = (new ParamExpression(/* PDivide */ 5, [expr_3, matchValue_5.fields[0][0]]));
                                remainingTokens_1_mut = matchValue_5.fields[0][1];
                                continue loop;
                            }
                        }
                        case 2: {
                            const matchValue_6 = parsePrimary(rest_12);
                            if (matchValue_6.tag === 1) {
                                return new FSharpResult$2(/* Error */ 1, [matchValue_6.fields[0]]);
                            }
                            else {
                                expr_3_mut = (new ParamExpression(/* PRemainder */ 6, [expr_3, matchValue_6.fields[0][0]]));
                                remainingTokens_1_mut = matchValue_6.fields[0][1];
                                continue loop;
                            }
                        }
                        default:
                            return new FSharpResult$2(/* Ok */ 0, [[expr_3, remainingTokens_1]]);
                    }
                    break;
                }
            };
            return loop(matchValue_3.fields[0][0], matchValue_3.fields[0][1]);
        }
    };
    const parseTerms = (tokens_3) => {
        const matchValue_7 = parseFactors(tokens_3);
        if (matchValue_7.tag === 1) {
            return new FSharpResult$2(/* Error */ 1, [matchValue_7.fields[0]]);
        }
        else {
            const loop_1 = (expr_4_mut, remainingTokens_2_mut) => {
                loop_1:
                while (true) {
                    const expr_4 = expr_4_mut, remainingTokens_2 = remainingTokens_2_mut;
                    let matchResult_4, rest_14, rest_15;
                    if (!isEmpty_1(remainingTokens_2)) {
                        switch (head(remainingTokens_2)) {
                            case "+": {
                                matchResult_4 = 0;
                                rest_14 = tail(remainingTokens_2);
                                break;
                            }
                            case "-": {
                                matchResult_4 = 1;
                                rest_15 = tail(remainingTokens_2);
                                break;
                            }
                            default:
                                matchResult_4 = 2;
                        }
                    }
                    else {
                        matchResult_4 = 2;
                    }
                    switch (matchResult_4) {
                        case 0: {
                            const matchValue_8 = parseFactors(rest_14);
                            if (matchValue_8.tag === 1) {
                                return new FSharpResult$2(/* Error */ 1, [matchValue_8.fields[0]]);
                            }
                            else {
                                expr_4_mut = (new ParamExpression(/* PAdd */ 2, [expr_4, matchValue_8.fields[0][0]]));
                                remainingTokens_2_mut = matchValue_8.fields[0][1];
                                continue loop_1;
                            }
                        }
                        case 1: {
                            const matchValue_9 = parseFactors(rest_15);
                            if (matchValue_9.tag === 1) {
                                return new FSharpResult$2(/* Error */ 1, [matchValue_9.fields[0]]);
                            }
                            else {
                                expr_4_mut = (new ParamExpression(/* PSubtract */ 3, [expr_4, matchValue_9.fields[0][0]]));
                                remainingTokens_2_mut = matchValue_9.fields[0][1];
                                continue loop_1;
                            }
                        }
                        default:
                            return new FSharpResult$2(/* Ok */ 0, [[expr_4, remainingTokens_2]]);
                    }
                    break;
                }
            };
            return loop_1(matchValue_7.fields[0][0], matchValue_7.fields[0][1]);
        }
    };
    const parseExpressionTokens = (tokens_4) => {
        const matchValue_10 = parseTerms(tokens_4);
        if (matchValue_10.tag === 1) {
            return new FSharpResult$2(/* Error */ 1, [matchValue_10.fields[0]]);
        }
        else {
            const loop_2 = (expr_5, remainingTokens_3) => {
                const shiftBy = (make, rest_17) => {
                    const matchValue_11 = parseTerms(rest_17);
                    if (matchValue_11.tag === 1) {
                        return new FSharpResult$2(/* Error */ 1, [matchValue_11.fields[0]]);
                    }
                    else {
                        return loop_2(make([expr_5, matchValue_11.fields[0][0]]), matchValue_11.fields[0][1]);
                    }
                };
                let matchResult_5, rest_18, rest_19;
                if (!isEmpty_1(remainingTokens_3)) {
                    switch (head(remainingTokens_3)) {
                        case "<<": {
                            matchResult_5 = 0;
                            rest_18 = tail(remainingTokens_3);
                            break;
                        }
                        case ">>": {
                            matchResult_5 = 1;
                            rest_19 = tail(remainingTokens_3);
                            break;
                        }
                        default:
                            matchResult_5 = 2;
                    }
                }
                else {
                    matchResult_5 = 2;
                }
                switch (matchResult_5) {
                    case 0:
                        return shiftBy((tupledArg_3) => (new ParamExpression(/* PShiftLeft */ 7, [tupledArg_3[0], tupledArg_3[1]])), rest_18);
                    case 1:
                        return shiftBy((tupledArg_4) => (new ParamExpression(/* PShiftRight */ 8, [tupledArg_4[0], tupledArg_4[1]])), rest_19);
                    default:
                        return new FSharpResult$2(/* Ok */ 0, [[expr_5, remainingTokens_3]]);
                }
            };
            return loop_2(matchValue_10.fields[0][0], matchValue_10.fields[0][1]);
        }
    };
    if (text === "") {
        return new FSharpResult$2(/* Error */ 1, [emptyInputError]);
    }
    else if (!isMatch(create("^[0-9a-zA-Z(),+\\-*/%<>\\s]+$"), text)) {
        const invalidChars = toArray(distinct(filter_1((c_1) => !isMatch(create("^[0-9a-zA-Z(),+\\-*/%<>\\s]+$"), c_1), text.split("")), {
            Equals: (x, y) => (x === y),
            GetHashCode: (x) => (stringHash(x) | 0),
        }));
        return new FSharpResult$2(/* Error */ 1, [toText(printf("Contains unsupported characters: %A"))(invalidChars)]);
    }
    else {
        const matchValue_13 = toList_1(map_3((m) => m[0], matches(create("0[xX][0-9a-fA-F]+|0[bB][01]+|\\d+|[a-zA-Z][a-zA-Z0-9]*|<<|>>|[(),+\\-*/%<>]"), text)));
        if (isEmpty_1(matchValue_13)) {
            return new FSharpResult$2(/* Error */ 1, [emptyInputError]);
        }
        else {
            const tokens_8 = matchValue_13;
            let matchValue_14;
            const tokens_7 = tokens_8;
            const matchValue_12 = tryFind((tupledArg_5) => {
                if (isMatch(create("^(0[xX][0-9a-fA-F]+|0[bB][01]+|\\d+)$"), tupledArg_5[0])) {
                    return isMatch(/^[a-zA-Z]/gu, tupledArg_5[1]);
                }
                else {
                    return false;
                }
            }, pairwise(tokens_7));
            if (matchValue_12 == null) {
                const option_1 = tryFind((token) => {
                    if (token === "<") {
                        return true;
                    }
                    else {
                        return token === ">";
                    }
                }, tokens_7);
                matchValue_14 = ((option_1 != null) ? ((bracket = option_1, `'${bracket}' is not an operator: a shift is written '${bracket}${bracket}'`)) : undefined);
            }
            else {
                const number = matchValue_12[0];
                const name_5 = matchValue_12[1];
                matchValue_14 = (`'${number}${name_5}' is neither a number nor a property name: a property name must start with a letter, and a multiplication must be written out as ${number}*${name_5}`);
            }
            if (matchValue_14 == null) {
                const matchValue_15 = parseExpressionTokens(tokens_8);
                if (matchValue_15.tag === 1) {
                    return new FSharpResult$2(/* Error */ 1, [matchValue_15.fields[0]]);
                }
                else if (!isEmpty_1(matchValue_15.fields[0][1])) {
                    if (head(matchValue_15.fields[0][1]) === ",") {
                        return new FSharpResult$2(/* Error */ 1, ["Unexpected comma: a comma separates the two values of min or max"]);
                    }
                    else {
                        return new FSharpResult$2(/* Error */ 1, [(arg_2 = join("", matchValue_15.fields[0][1]), toText(printf("Unexpected characters at end of expression: %s"))(arg_2))]);
                    }
                }
                else {
                    return new FSharpResult$2(/* Ok */ 0, [matchValue_15.fields[0][0]]);
                }
            }
            else {
                return new FSharpResult$2(/* Error */ 1, [matchValue_14]);
            }
        }
    }
}

/**
 * Checks if a parameter expression contains any parameter references.
 */
export function exprContainsParams(expression_mut) {
    exprContainsParams:
    while (true) {
        const expression = expression_mut;
        let matchResult, left, right;
        switch (expression.tag) {
            case 1: {
                matchResult = 1;
                break;
            }
            case 2: {
                matchResult = 2;
                left = expression.fields[0];
                right = expression.fields[1];
                break;
            }
            case 3: {
                matchResult = 2;
                left = expression.fields[0];
                right = expression.fields[1];
                break;
            }
            case 4: {
                matchResult = 2;
                left = expression.fields[0];
                right = expression.fields[1];
                break;
            }
            case 5: {
                matchResult = 2;
                left = expression.fields[0];
                right = expression.fields[1];
                break;
            }
            case 6: {
                matchResult = 2;
                left = expression.fields[0];
                right = expression.fields[1];
                break;
            }
            case 7: {
                matchResult = 2;
                left = expression.fields[0];
                right = expression.fields[1];
                break;
            }
            case 8: {
                matchResult = 2;
                left = expression.fields[0];
                right = expression.fields[1];
                break;
            }
            case 10: {
                matchResult = 2;
                left = expression.fields[1];
                right = expression.fields[2];
                break;
            }
            case 9: {
                matchResult = 3;
                break;
            }
            default:
                matchResult = 0;
        }
        switch (matchResult) {
            case 0:
                return false;
            case 1:
                return true;
            case 2:
                if (exprContainsParams(left)) {
                    return true;
                }
                else {
                    expression_mut = right;
                    continue exprContainsParams;
                }
            default: {
                expression_mut = expression.fields[0];
                continue exprContainsParams;
            }
        }
        break;
    }
}

/**
 * The parameters an expression refers to, without duplicates.
 * Used to check the invariant that every parameter referred to on a sheet is defined on that sheet.
 */
export function paramNamesOfExpr(expression) {
    const collect = (expression_1_mut) => {
        collect:
        while (true) {
            const expression_1 = expression_1_mut;
            let matchResult, left, right;
            switch (expression_1.tag) {
                case 1: {
                    matchResult = 1;
                    break;
                }
                case 2: {
                    matchResult = 2;
                    left = expression_1.fields[0];
                    right = expression_1.fields[1];
                    break;
                }
                case 3: {
                    matchResult = 2;
                    left = expression_1.fields[0];
                    right = expression_1.fields[1];
                    break;
                }
                case 4: {
                    matchResult = 2;
                    left = expression_1.fields[0];
                    right = expression_1.fields[1];
                    break;
                }
                case 5: {
                    matchResult = 2;
                    left = expression_1.fields[0];
                    right = expression_1.fields[1];
                    break;
                }
                case 6: {
                    matchResult = 2;
                    left = expression_1.fields[0];
                    right = expression_1.fields[1];
                    break;
                }
                case 7: {
                    matchResult = 2;
                    left = expression_1.fields[0];
                    right = expression_1.fields[1];
                    break;
                }
                case 8: {
                    matchResult = 2;
                    left = expression_1.fields[0];
                    right = expression_1.fields[1];
                    break;
                }
                case 10: {
                    matchResult = 2;
                    left = expression_1.fields[1];
                    right = expression_1.fields[2];
                    break;
                }
                case 9: {
                    matchResult = 3;
                    break;
                }
                default:
                    matchResult = 0;
            }
            switch (matchResult) {
                case 0:
                    return empty();
                case 1:
                    return singleton(expression_1.fields[0]);
                case 2:
                    return append(collect(left), collect(right));
                default: {
                    expression_1_mut = expression_1.fields[0];
                    continue collect;
                }
            }
            break;
        }
    };
    return List_distinct(collect(expression), {
        Equals: equals_1,
        GetHashCode: (x) => (safeHash(x) | 0),
    });
}

/**
 * The parameters a slot refers to: its value expression and the expressions in its constraints.
 */
export function paramNamesOfSlot(exprSpec) {
    let list_2;
    return List_distinct((list_2 = collect_1((konst) => {
        let expr;
        if (konst.tag === 1) {
            expr = konst.fields[0];
        }
        else {
            expr = konst.fields[0];
        }
        return paramNamesOfExpr(expr);
    }, exprSpec.Constraints), append(paramNamesOfExpr(exprSpec.Expression), list_2)), {
        Equals: equals_1,
        GetHashCode: (x) => (safeHash(x) | 0),
    });
}

/**
 * The slots of one sheet that refer to the named parameter of that sheet.
 * A custom component instance is not a special case: its CustomCompParam slot holds an expression
 * in the parameters of the sheet the instance sits on, like any other slot.
 */
export function slotsUsingParam(name, slots) {
    return filter_2((tupledArg) => contains(name, paramNamesOfSlot(tupledArg[1]), {
        Equals: equals_1,
        GetHashCode: (x) => (safeHash(x) | 0),
    }), toList(slots));
}

